import { Link, useLocation } from "react-router-dom";
import { useSiteName } from "@/hooks/useSiteSettings";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import CheckoutButton from "@/components/CheckoutButton";
import { lazy, Suspense, useState, useEffect } from "react";
import { Menu, X, Bell, Shield, Sparkles, Lock } from "lucide-react";
import { useAiAssistant } from "@/contexts/AiAssistantContext";
import { UserAvatarButton } from "@/components/UserAvatarButton";
// 128px copy of the mascot: shown at 36-56px, the 512px PNG was 270 KB.
import bullioFace from "@/assets/bullio-face-sm.webp";
import { useUserRole } from "@/hooks/useUserRole";

// Only signed-in visitors open this menu, so its dropdown code is its own file.
const UserMenu = lazy(() => import("@/components/UserMenu"));

const Navbar = () => {
  // Admin-managed in Settings; was hardcoded in both places before.
  const siteName = useSiteName();
  const { user, signOut } = useAuth();
  const { isAdmin } = useUserRole();
  const { openAssistant } = useAiAssistant();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    // The first reading waits for a frame to be drawn. Read straight away, in
    // the same task as the first render, scrollY forced an extra layout.
    const raf = requestAnimationFrame(() => requestAnimationFrame(onScroll));
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const isActive = (path: string) => location.pathname === path;

  // Not a real route yet — shown locked so visitors know it's coming rather
  // than wondering why it's missing. Flip `locked` to false once the page
  // behind "/live-trading-rooms" ships.
  const liveTradingRoomsLink = {
    to: "/live-trading-rooms",
    label: "Live Trading Rooms",
    locked: true,
  };

  const publicLinks = [
    { to: "/", label: "Home" },
    { to: "/courses", label: "Courses" },
    { to: "/curriculum", label: "Curriculum" },
    { to: "/testimonials", label: "Testimonials" },
    { to: "/pricing", label: "Pricing" },
    { to: "/about", label: "About" },
    { to: "/insights", label: "Insights" },
    liveTradingRoomsLink,
    { to: "/contact", label: "Contact" },
  ];

  const studentLinks = [
    { to: "/dashboard", label: "Dashboard" },
    { to: "/courses", label: "My Courses" },
    { to: "/curriculum", label: "Curriculum" },
    { to: "/insights", label: "Insights" },
    { to: "/news", label: "News" },
    liveTradingRoomsLink,
  ];

  const links = user ? studentLinks : publicLinks;
  const initials = user?.user_metadata?.full_name
    ? user.user_metadata.full_name.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() || "U";

  return (
    <nav
      className={`sticky top-0 z-50 border-b transition-all duration-300 ${
        scrolled
          ? "border-border bg-background/80 backdrop-blur-xl shadow-[0_4px_24px_-12px_hsl(220_42%_20%/0.18)]"
          : "border-transparent bg-background/95 backdrop-blur-sm"
      }`}
    >
      <div className="container-page flex items-center justify-between px-4 py-3 sm:px-6">
        <Link to="/" className="flex items-center gap-2.5 group">
          <img
            src={bullioFace}
            alt=""
            width={40}
            height={40}
            className="h-9 w-9 rounded-xl object-cover shadow-md ring-2 ring-secondary/20 transition-transform group-hover:scale-110 group-hover:rotate-3 sm:h-11 sm:w-11"
          />
          <span className="text-base font-bold tracking-tight text-primary sm:text-xl">
            {siteName}
          </span>
        </Link>

        {/* Desktop */}
        <div className="hidden items-center gap-1 lg:flex">
          {links.map((l) =>
            "locked" in l && l.locked ? (
              <span
                key={l.to + l.label}
                className="inline-flex cursor-not-allowed items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground/50"
                title="Coming soon"
                aria-disabled="true"
              >
                <Lock size={13} />
                {l.label}
              </span>
            ) : (
              <Link
                key={l.to + l.label}
                to={l.to}
                className={`whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive(l.to)
                    ? "bg-secondary/10 text-[#2f6e51]"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {l.label}
              </Link>
            )
          )}

          {user ? (
            <div className="ml-3 flex items-center gap-2">
              <button
                onClick={openAssistant}
                className="flex items-center gap-1.5 rounded-lg border border-secondary/20 bg-secondary/5 px-3 py-2 text-xs font-semibold text-secondary transition-colors hover:bg-secondary/10"
                aria-label="Open AI Assistant"
              >
                <Sparkles size={14} /> Ask Bullio
              </button>
              <button className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                <Bell size={18} />
              </button>
              <Suspense fallback={<UserAvatarButton initials={initials} />}>
                <UserMenu initials={initials} isAdmin={isAdmin} signOut={signOut} />
              </Suspense>
            </div>
          ) : (
            <div className="ml-3 flex items-center gap-2">
              <Link to="/login">
                <Button variant="outline" size="sm">Login</Button>
              </Link>
              <CheckoutButton size="sm" className="bg-secondary hover:bg-secondary/90" />
            </div>
          )}
        </div>

        {/* Mobile: AI button + menu toggle */}
        <div className="flex items-center gap-1 lg:hidden">
          <button
            onClick={openAssistant}
            className="flex h-9 items-center gap-1.5 rounded-lg border border-secondary/20 bg-secondary/5 px-2.5 text-xs font-semibold text-[#2f6e51] transition-colors hover:bg-secondary/10"
            aria-label="Open AI Assistant"
          >
            <Sparkles size={14} />
            <span className="hidden sm:inline">Ask Bullio</span>
            <span className="sm:hidden">AI</span>
          </button>
          <button
            className="flex h-10 w-10 items-center justify-center text-foreground -mr-2"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="border-t border-border px-3 pb-4 pt-2 lg:hidden bg-background max-h-[calc(100vh-60px)] overflow-y-auto">
          {links.map((l) =>
            "locked" in l && l.locked ? (
              <span
                key={l.to + l.label}
                className="flex cursor-not-allowed items-center gap-2 rounded-lg px-3 py-3 text-base font-medium text-muted-foreground/50"
                title="Coming soon"
                aria-disabled="true"
              >
                <Lock size={15} />
                {l.label}
              </span>
            ) : (
              <Link
                key={l.to + l.label}
                to={l.to}
                onClick={() => setOpen(false)}
                className={`block rounded-lg px-3 py-3 text-base font-medium ${
                  isActive(l.to)
                    ? "bg-secondary/10 text-[#2f6e51]"
                    : "text-muted-foreground hover:bg-muted"
                }`}
              >
                {l.label}
              </Link>
            )
          )}
          {user ? (
            <>
              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setOpen(false)}
                  className="mt-2 flex items-center gap-2 rounded-lg px-3 py-3 text-base font-medium text-secondary hover:bg-muted"
                >
                  <Shield size={16} /> Admin
                </Link>
              )}
              <button
                onClick={() => { signOut(); setOpen(false); }}
                className="mt-2 block w-full rounded-lg px-3 py-3 text-left text-base text-destructive font-medium hover:bg-muted"
              >
                Sign Out
              </button>
            </>
          ) : (
            <div className="mt-3 grid grid-cols-2 gap-2 px-1">
              <Link to="/login" onClick={() => setOpen(false)} className="w-full">
                <Button variant="outline" className="w-full">Login</Button>
              </Link>
              <CheckoutButton
                wrapperClassName="w-full"
                className="w-full bg-secondary hover:bg-secondary/90"
                label="Enroll"
              />
            </div>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
